import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Button, Card, CardContent, Chip, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useRecordContext, type RaRecord } from 'react-admin';
import { callAction } from './dataProvider';

interface Named { id: number; name: string; code?: string | null }
interface Classification {
  producers: Named[];
  categories: Named[];
  printers: (Named & { code: string | null })[];
  buyoutProducts: (Named & { code: string | null })[];
}

type Kind = 'producer' | 'category' | 'printer' | 'buyout';

/**
 * Zaradenie — what a product is and what it fits.
 *
 * Worth more than it looks: three of the products list's filters read straight
 * out of these tables, so a product with no producer is invisible under the
 * "assigned" filter.
 */
function Section({ title, kind, items, options, productId, onChanged, hint }: {
  title: string;
  kind: Kind;
  items: (Named & { code?: string | null })[];
  options: Named[];
  productId: number;
  onChanged: () => void;
  hint?: string;
}) {
  const notify = useNotify();
  const [choice, setChoice] = useState<Named | null>(null);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (choice == null) return;
    setBusy(true);
    try {
      await callAction(`products/${productId}/classification`, 'POST', { kind, targetId: choice.id });
      setChoice(null);
      onChanged();
    } catch {
      notify(`Položku sa nepodarilo pridať: ${kind}.`, { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (targetId: number) => {
    setBusy(true);
    try {
      await callAction(`products/${productId}/classification/${kind}/${targetId}`, 'DELETE');
      onChanged();
    } catch {
      notify(`Položku sa nepodarilo odobrať: ${kind}.`, { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        {hint != null && <Typography variant="caption" display="block" color="text.secondary">{hint}</Typography>}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5, mb: 1.5 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            options={options}
            value={choice}
            onChange={(_event, value) => setChoice(value)}
            getOptionLabel={option => (option.code != null ? `${option.name} (${option.code})` : option.name)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => <TextField {...params} label={title} />}
          />
          <Button variant="outlined" size="small" disabled={choice == null || busy} onClick={() => void add()}>
            Pridať
          </Button>
        </Stack>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Nič nie je priradené.</Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {items.map(item => (
              <Chip
                key={item.id}
                label={item.code != null ? `${item.name || item.code} (${item.code})` : item.name}
                onDelete={() => void remove(item.id)}
                deleteIcon={<DeleteIcon />}
                disabled={busy}
              />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export function ClassificationTab() {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const [data, setData] = useState<Classification | null>(null);
  const [categories, setCategories] = useState<Named[]>([]);
  const [organizations, setOrganizations] = useState<Named[]>([]);
  const [products, setProducts] = useState<Named[]>([]);

  const load = useCallback(() => {
    if (record == null) return;
    callAction(`products/${record.id}/classification`, 'GET' as never)
      .then(value => setData(value as Classification))
      .catch(() => notify('Zaradenie sa nepodarilo načítať.', { type: 'warning' }));
  }, [record?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    callAction('categories', 'GET' as never)
      .then(value => setCategories((value as { data: Named[] }).data))
      .catch(() => undefined);
    callAction('organizations?_perPage=200&_sortField=name&_sortOrder=asc', 'GET' as never)
      .then(value => setOrganizations((value as { data: Named[] }).data))
      .catch(() => undefined);
    // Printers and buyout shells are both products; a wide page is enough for
    // a picker without turning it into a search screen.
    callAction('products?_perPage=200', 'GET' as never)
      .then(value => setProducts((value as { data: { id: number; code: string | null; heading: string | null }[] }).data
        .map(item => ({ id: item.id, name: item.heading ?? item.code ?? `#${item.id}`, code: item.code }))))
      .catch(() => undefined);
  }, []);

  if (record == null || data == null) return <Typography variant="body2">Načítava sa…</Typography>;

  return (
    <Stack spacing={2}>
      <Section
        title="Výrobca" kind="producer" items={data.producers} options={organizations}
        productId={Number(record.id)} onChanged={load}
        hint="Drives the products list's producer filter — an unassigned product hides under it."
      />
      <Section
        title="Kategórie" kind="category" items={data.categories} options={categories}
        productId={Number(record.id)} onChanged={load}
      />
      <Section
        title="Tlačiarne" kind="printer" items={data.printers} options={products}
        productId={Number(record.id)} onChanged={load}
        hint="Printers this product fits. Stored printer-first, so this is the reverse lookup."
      />
      <Section
        title="Výkupné produkty" kind="buyout" items={data.buyoutProducts} options={products}
        productId={Number(record.id)} onChanged={load}
        hint="The empty shells this product comes back as. Its buyback price is on the Ceny tab."
      />
    </Stack>
  );
}
